import { supabase } from "@/lib/supabase";

export type NotificationType =
  | "poll_to_match"
  | "reserve_promoted"
  | "quorum_reached"
  | "result_published"
  | "result_disputed";

export interface PushSubscriptionRow {
  id: string;
  group_id: string;
  user_id: string;
  endpoint: string;
  p256dh: string;
  auth_key: string;
}

export async function savePushSubscription(
  groupId: string,
  subscription: PushSubscriptionJSON
): Promise<void> {
  const keys = subscription.keys;
  if (!keys?.p256dh || !keys?.auth) throw new Error("INVALID_SUBSCRIPTION");

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id;
  if (!userId) throw new Error("NOT_AUTHENTICATED");

  const { error } = await supabase.from("push_subscriptions").upsert(
    {
      group_id: groupId,
      user_id: userId,
      endpoint: subscription.endpoint,
      p256dh: keys.p256dh,
      auth_key: keys.auth,
    },
    { onConflict: "endpoint" }
  );

  if (error) throw error;
}

export async function removePushSubscriptionsForGroup(groupId: string): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id;
  if (!userId) return;

  const { error } = await supabase
    .from("push_subscriptions")
    .delete()
    .eq("group_id", groupId)
    .eq("user_id", userId);

  if (error) throw error;
}

export async function dispatchNotification(payload: {
  groupId: string;
  type: NotificationType;
  eventId?: string;
  userId?: string;
  actorUserId?: string;
  title: string;
  body: string;
  url?: string;
}): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  try {
    await fetch(`${supabaseUrl}/functions/v1/send-push`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.warn("dispatchNotification failed:", err);
  }
}

export async function wasNotificationSent(
  groupId: string,
  type: NotificationType,
  eventId?: string
): Promise<boolean> {
  let query = supabase
    .from("notification_log")
    .select("id")
    .eq("group_id", groupId)
    .eq("notification_type", type)
    .limit(1);

  if (eventId) query = query.eq("event_id", eventId);

  const { data, error } = await query.maybeSingle();
  if (error) return false;
  return Boolean(data);
}

export async function logNotificationSent(
  groupId: string,
  type: NotificationType,
  eventId?: string,
  userId?: string
): Promise<void> {
  await supabase.from("notification_log").insert({
    group_id: groupId,
    notification_type: type,
    event_id: eventId ?? null,
    user_id: userId ?? null,
  });
}
